(function (global) {
  'use strict';
  function groupLines(cart, offer, variants) {
    const lines = cart.items.filter(line => String(line.properties?._pdp_kit_offer || '') === offer && String(line.properties?._pdp_kit_variants || '') === variants);
    const ids = lines.map(line => String(line.variant_id));
    const expected = variants ? variants.split(',') : ids;
    if (lines.length && lines.every(line => line.properties?._pdp_kit_schema === 'slots-v1')) {
      const quantities = expected.map(() => 0);
      lines.forEach(line => {
        const slot = Number(line.properties._pdp_kit_slot);
        if (!Number.isInteger(slot) || String(slot) !== line.properties._pdp_kit_slot || expected[slot] !== String(line.variant_id) || !Number.isSafeInteger(line.quantity) || line.quantity < 1) throw new Error('Kit composition changed');
        quantities[slot] += line.quantity;
      });
      if (quantities.some(quantity => quantity < 1 || quantity !== quantities[0])) throw new Error('Kit composition changed');
      return lines;
    }
    const legacyDiscount = lines.some(line => (line.line_level_discount_allocations || []).some(allocation => allocation.discount_application?.title?.startsWith('PDP kit | ') || allocation.discount_application?.title?.includes(' — Save ')));
    if (lines.length < 2 || (!variants && (lines.length !== 2 || !legacyDiscount)) || new Set(ids).size !== ids.length || expected.length !== lines.length || expected.some(id => !ids.includes(id)) || lines.some(line => line.quantity !== lines[0].quantity)) throw new Error('Kit composition changed');
    return lines;
  }
  function buildUpdates(cart, offer, variants, quantity) {
    if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Invalid quantity');
    const lines = groupLines(cart, offer, variants);
    if (lines[0].properties?._pdp_kit_schema === 'slots-v1') {
      const used = new Set();
      return Object.fromEntries(lines.map(line => {const slot=line.properties._pdp_kit_slot;const next=used.has(slot)?0:quantity;used.add(slot);return [line.key,next];}));
    }
    return Object.fromEntries(lines.map(line => [line.key, quantity]));
  }
  function matchingLines(cart, offer, variants) {
    return cart.items.filter(line => String(line.properties?._pdp_kit_offer || '') === offer && String(line.properties?._pdp_kit_variants || '') === variants);
  }
  function lineIdentity(line) {
    const properties = Object.entries(line.properties || {}).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify([String(line.variant_id), String(line.selling_plan_allocation?.selling_plan?.id || ''), properties]);
  }
  function totalsByIdentity(lines) {
    const totals = new Map();
    lines.forEach(line => {
      if (!Number.isSafeInteger(line.quantity) || line.quantity < 1 || !Number.isSafeInteger(line.final_line_price) || line.final_line_price < 0) throw new Error('Cart prices unavailable');
      const id = lineIdentity(line), prior = totals.get(id) || {quantity: 0, price: 0};
      totals.set(id, {quantity: prior.quantity + line.quantity, price: prior.price + line.final_line_price});
    });
    return totals;
  }
  function upgradeSource(cart, quote) {
    const source = cart.items.find(line => line.key === quote.sourceKey);
    if (!source || source.quantity !== quote.quantityDelta || source.properties?._pdp_kit_offer || source.selling_plan_allocation || source.item_components?.length) throw new Error('Upgrade item changed');
    return source;
  }
  function assertUpgrade(before, after, quote) {
    const source = upgradeSource(before, quote);
    // Model the operation as replacing precisely one original line with a new complete kit.
    const withoutSource = {...before, items: before.items.filter(line => line !== source), total_price: before.total_price - source.final_line_price};
    return assertQuote(withoutSource, after, {...quote, sourceKey: undefined});
  }
  function upgradeRestorePlan(before, current, quote) {
    const source = upgradeSource(before, quote), sourceId = lineIdentity(source);
    const target = line => lineIdentity(line) === sourceId || matchingLines({items:[line]}, quote.offer, quote.variants).length;
    const remaining = new Map();
    before.items.filter(target).forEach(line => remaining.set(lineIdentity(line), (remaining.get(lineIdentity(line)) || 0) + line.quantity));
    const updates = {};
    current.items.filter(target).forEach(line => {
      const id = lineIdentity(line), quantity = Math.min(line.quantity, remaining.get(id) || 0);
      updates[line.key] = quantity; remaining.set(id, (remaining.get(id) || 0) - quantity);
    });
    const missing = [];
    for (const [id, quantity] of remaining) {
      if (!quantity) continue;
      const original = before.items.find(line => lineIdentity(line) === id);
      missing.push({id: original.variant_id, quantity, properties: original.properties || {}});
    }
    return {updates, missing};
  }
  function assertQuote(before, after, quote) {
    if (quote.sourceKey) return assertUpgrade(before, after, quote);
    const {offer, variants, unitPrice, quantityDelta} = quote;
    if (!Number.isSafeInteger(unitPrice) || unitPrice < 0 || !Number.isSafeInteger(quantityDelta) || quantityDelta < 1) throw new Error('Invalid kit quote');
    const group = groupLines(after, offer, variants);
    const previous = matchingLines(before, offer, variants);
    if (previous.length) groupLines(before, offer, variants);
    const units = lines => lines[0]?.properties?._pdp_kit_schema === 'slots-v1' ? lines.filter(line => line.properties._pdp_kit_slot === '0').reduce((sum, line) => sum + line.quantity, 0) : (lines[0]?.quantity || 0);
    const nextUnits = units(group), priorUnits = units(previous);
    if (nextUnits - priorUnits !== quantityDelta) throw new Error('Kit quantity changed');
    const groupTotal = group.reduce((sum, line) => sum + line.final_line_price, 0);
    if (![before.total_price, after.total_price, groupTotal].every(value => Number.isSafeInteger(value) && value >= 0)) throw new Error('Cart prices unavailable');
    // A cheaper group can conceal a discount removed from another item. Check both totals.
    if (groupTotal > unitPrice * nextUnits || after.total_price - before.total_price > unitPrice * quantityDelta) throw new Error('Kit price does not match the quote');
    const unrelated = cart => cart.items.filter(line => !matchingLines(cart, offer, variants).includes(line));
    const priorOther = totalsByIdentity(unrelated(before)), nextOther = totalsByIdentity(unrelated(after));
    if (priorOther.size !== nextOther.size) throw new Error('Other cart items changed');
    for (const [id, prior] of priorOther) {
      const next = nextOther.get(id);
      if (!next || next.quantity !== prior.quantity || next.price > prior.price) throw new Error('An existing cart discount changed');
    }
    return after;
  }
  function restoreUpdates(before, current, offer, variants) {
    const remaining = new Map();
    matchingLines(before, offer, variants).forEach(line => remaining.set(lineIdentity(line), (remaining.get(lineIdentity(line)) || 0) + line.quantity));
    const updates = {};
    matchingLines(current, offer, variants).forEach(line => {
      const id = lineIdentity(line), quantity = Math.min(line.quantity, remaining.get(id) || 0);
      updates[line.key] = quantity; remaining.set(id, (remaining.get(id) || 0) - quantity);
    });
    if ([...remaining.values()].some(quantity => quantity > 0)) throw new Error('Original kit cannot be restored');
    return updates;
  }
  function assertRestored(before, restored) {
    const prior = totalsByIdentity(before.items), next = totalsByIdentity(restored.items);
    if (!Number.isSafeInteger(restored.total_price) || restored.total_price > before.total_price || prior.size !== next.size) throw new Error('Cart could not be restored');
    for (const [id, line] of prior) {
      const restoredLine = next.get(id);
      if (!restoredLine || restoredLine.quantity !== line.quantity || restoredLine.price > line.price) throw new Error('Cart could not be restored');
    }
  }
  async function runPricedMutation({readCart, mutate, restoreCart, ...quote}) {
    const before = await readCart();
    if (quote.sourceKey) upgradeSource(before, quote);
    try {
      await mutate(before);
      return assertQuote(before, await readCart(), quote);
    } catch (error) {
      // Also reconcile an unknown mutation outcome; never blindly repeat a cart add.
      try {
        await restoreCart(before, await readCart(), quote);
        assertRestored(before, await readCart());
      }
      catch (restoreError) { error.rollbackFailed = true; error.restoreError = restoreError; }
      throw error;
    }
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = {groupLines, buildUpdates, assertQuote, restoreUpdates, runPricedMutation, upgradeSource, upgradeRestorePlan}; return; }
  if (global.PdpKitCart) return;
  global.PdpKitCart = {groupLines, buildUpdates, transact, upgradeSource};
  let busy = false;
  let transactionBusy = false;
  const root = global.Shopify?.routes?.root || '/';
  async function readCart() {
    if (global.theme?.cartReads) return global.theme.cartReads.read();
    const response = await fetch(`${root}cart.js`, {credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}});
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  }
  async function postCart(payload) {
    const response = await (global.theme?.cartReads?.request || fetch)(`${root}cart/update.js`, {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(payload)});
    const result = await response.json();
    if (!response.ok || result.status) throw new Error('Cart update failed');
    return result;
  }
  async function transact(quote, mutate, {restoreDiscountCodes = false} = {}) {
    if (transactionBusy) throw new Error('Cart is busy');
    transactionBusy = true;
    document.documentElement.setAttribute('data-pdp-kit-cart-busy', 'true');
    try {
      return await runPricedMutation({ ...quote, readCart, mutate, restoreCart: async (before, current) => {
        const plan = quote.sourceKey ? upgradeRestorePlan(before, current, quote) : {updates:restoreUpdates(before, current, quote.offer, quote.variants), missing:[]};
        const payload = {updates:plan.updates};
        if (restoreDiscountCodes) payload.discount = (before.discount_codes || []).map(code => code.code).join(',');
        await postCart(payload);
        if (plan.missing.length) {
          // Re-read after the update: a prior uncertain request must never cause a duplicate restore.
          const refreshed = await readCart();
          const missing = upgradeRestorePlan(before, refreshed, quote).missing;
          if (missing.length) {
            const response = await fetch(`${root}cart/add.js`, {method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json',Accept:'application/json'}, body:JSON.stringify({items:missing})});
            if (!response.ok) throw new Error('Original item could not be restored');
          }
        }
        document.dispatchEvent(new CustomEvent('cart:build'));
        if (typeof global.refreshCartDrawer === 'function') await global.refreshCartDrawer();
      }});
    } catch (error) {
      if (error.rollbackFailed) {
        document.documentElement.setAttribute('data-pdp-kit-pricing-error', 'true');
        document.querySelectorAll('[name="checkout"]').forEach(button => {button.disabled = true;});
      }
      throw error;
    } finally {
      transactionBusy = false;
      if (!busy) document.documentElement.removeAttribute('data-pdp-kit-cart-busy');
    }
  }
  function guardCheckout(event) {
    const locked = transactionBusy || busy || document.documentElement.hasAttribute('data-pdp-kit-pricing-error');
    if (!locked) return;
    const target = event.target;
    const checkout = event.type === 'submit'
      ? event.submitter?.name === 'checkout' || /\/checkout(?:[/?]|$)/.test(target.action || '')
      : target.closest('[name="checkout"], a[href^="/checkout"], .additional-checkout-buttons');
    if (checkout) {event.preventDefault(); event.stopImmediatePropagation();}
  }
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
  async function change(group, action) {
    if (busy || transactionBusy) return;
    const input = group.querySelector('[data-pdp-kit-quantity]');
    const status = group.querySelector('[role="status"]');
    const checkout = [...document.querySelectorAll('[name="checkout"]')].map(button => [button, button.disabled]);
    busy = true; document.documentElement.setAttribute('data-pdp-kit-cart-busy','true'); group.setAttribute('aria-busy', 'true'); status.hidden = true;
    checkout.forEach(([button]) => {button.disabled = true;});
    try {
      const cart = window.theme?.cartReads ? await window.theme.cartReads.read() : await fetch(`${root}cart.js`, {credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}}).then(response => {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.json();
      });
      const lines = groupLines(cart, group.dataset.pdpKitOffer, group.dataset.pdpKitVariants);
      const currentQuantity = lines[0].properties?._pdp_kit_schema === 'slots-v1' ? lines.filter(line=>line.properties._pdp_kit_slot === '0').reduce((sum,line)=>sum+line.quantity,0) : lines[0].quantity;
      const quantity = action === 'remove' ? 0 : action === 'plus' ? currentQuantity + 1 : action === 'minus' ? Math.max(0, currentQuantity - 1) : Number(input.value);
      const updates = buildUpdates(cart, group.dataset.pdpKitOffer, group.dataset.pdpKitVariants, quantity);
      const next = quantity > currentQuantity
        ? await transact({offer: group.dataset.pdpKitOffer, variants: group.dataset.pdpKitVariants, unitPrice: Number(group.dataset.pdpKitUnitPrice), quantityDelta: quantity - currentQuantity}, () => postCart({updates}))
        : await postCart({updates});
      document.dispatchEvent(new CustomEvent('cart:build'));
      if (typeof global.refreshCartDrawer === 'function') await global.refreshCartDrawer();
      else document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated', {detail: {cart: next}}));
    } catch (error) {
      status.textContent = group.dataset.pdpKitError; status.hidden = false;
    } finally {
      busy = false; document.documentElement.removeAttribute('data-pdp-kit-cart-busy'); group.removeAttribute('aria-busy');
      checkout.forEach(([button, disabled]) => {if (button.isConnected) button.disabled = disabled || document.documentElement.hasAttribute('data-pdp-kit-pricing-error');});
    }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-pdp-kit-action]');
    if (!button) return;
    const group = button.closest('[data-pdp-kit-group]');
    if (!group) return;
    event.preventDefault(); change(group, button.dataset.pdpKitAction);
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-pdp-kit-quantity]')) change(event.target.closest('[data-pdp-kit-group]'), 'input');
  });
})(typeof window !== 'undefined' ? window : globalThis);
